import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import DemoCTA from '../../ui/DemoCTA';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './B2CB2B.css';

const B2CB2B = () => {
  React.useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });
  }, []);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          AI Chatbot for B2C and D2C brands in SG, UK & UAE with Conversion Optimization
        </title>
        <meta
          name="description"
          content="AI-assisted chatbot for Multi-Channel Presence with Analytics Dashboard for B2B & D2C brands helping businesses automate support, generate leads, and create personalized customer experiences."
        />
        <meta
          name="keywords"
          content="B2C & D2C Brand Solutions, WebsiteAI Chatbot for your Business, B2C and D2C brands, ai based customer support, AI Insights, marketing campaigns with AI"
        />
        <link rel="canonical" href="https://www.aichatassist.com/industries/b2c-d2c-solutions" />

        {/* Open Graph Meta */}
        <meta property="og:title" content="WebsiteAI Chatbot for Brands | AI Chat Assist Global" />
        <meta
          property="og:description"
          content="Engage customers with AI Chat Assist’s chatbot solutions for B2C & D2C brands. Trusted by businesses in Singapore, UK & UAE for smart automation."
        />
        <meta
          property="og:url"
          content="https://www.aichatassist.com/industries/b2c-d2c-solutions"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Meta */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WebsiteAI Chatbot for Brands | AI Chat Assist Global" />
        <meta
          name="twitter:description"
          content="Engage customers with AI Chat Assist’s chatbot solutions for B2C & D2C brands. Trusted by businesses in Singapore, UK & UAE for smart automation."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section" style={{ marginTop: '-80px', paddingTop: 0 }}>
        <div className="container">
          <div className="hero-wrapper">
            <div className="hero-content">
              <h1 className="hero-heading mb-4 animate-up">
                <span className="position-relative">Website AI Chatbot</span>
                <span className="hero-highlight"> for B2C and D2C Brands</span>
              </h1>
              <p>
                AI-assisted chatbot solutions designed for B2C & D2C Brand Solutions. Automate
                support, generate leads, and personalize customer journeys across channels with AI
                Insights.
              </p>
            </div>
            <div className="hero-image slide-in-right">
              <img src="/images/wbsection/bussbanner.png" alt="B2C & d2c Solutions" className="" />
            </div>
          </div>
        </div>
        <div className="shape-divider"></div>
      </section>

      {/* Key Challenges Section */}
      <section className="section-padding">
        <div className="container">
          <div className="section-header text-center fade-in">
            <h2>
              Key <span className="gradient-text">Challenges</span>
            </h2>
            <p>
              Top hurdles faced by modern B2C & D2C Brand Solutions and how AI-based customer
              support can help.
            </p>
          </div>

          <div className="platform-grid mt-5">
            <div className="platform-card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                </svg>
              </div>
              <h3>24/7 Customer Expectations</h3>
              <p>
                B2C and D2C brands face pressure to deliver real-time responses 24/7. AI-based
                customer support helps eliminate service gaps and staffing strain.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3>Multi-Channel Presence</h3>
              <p>
                B2C & D2C Brand Solutions must unify customer experiences across website, social
                apps, and messaging. AI simplifies cross-channel consistency.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                </svg>
              </div>
              <h3>Rising Support Costs</h3>
              <p>
                Seasonal spikes overwhelm support teams. AI-based customer support allows brands to
                scale service without rising costs.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <h3>Lead Qualification</h3>
              <p>
                Qualifying leads fast is vital for B2C and D2C brands. AI chatbots identify
                high-value prospects early to improve conversion chances.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h3>Personalization at Scale</h3>
              <p>
                B2C & D2C Brand Solutions need to deliver personal touches at scale. AI Insights
                enable tailored experiences across the customer journey.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.6s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3>Complex Sales Cycles</h3>
              <p>
                D2C brands face long, multi-touch sales cycles. AI chatbots streamline nurturing
                through personalized, timely follow-ups.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.7s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Conversion Optimization</h3>
              <p>
                Boosting conversions for B2C and D2C brands requires smart engagement. AI-assisted
                chatbots guide users from visit to purchase.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.8s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  <path d="M6 8h2"></path>
                  <path d="M6 12h4"></path>
                  <path d="M16 8h2"></path>
                  <path d="M16 12h2"></path>
                </svg>
              </div>
              <h3>Customer Education</h3>
              <p>
                AI helps educate customers without overload. D2C brands can simplify product
                decisions through automated, conversational guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="b2cb2b-usecases-section">
        <div className="b2cb2b-usecases-bg"></div>
        <div className="container">
          <div className="b2cb2b-usecases-header text-center">
            <span className="b2cb2b-usecases-subtitle">OUR USE CASES</span>
            <h2>
              Why you should choose <span className="hero-highlight">AI Chat Assist</span>
            </h2>
          </div>

          <div className="b2cb2b-usecases-grid">
            <div className="b2cb2b-usecase-card" data-aos="fade-up" data-aos-delay="100">
              <div className="b2cb2b-usecase-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Lead Generation</h3>
              <p>
                Turn traffic into leads with WebsiteAI Chatbot for your Business. Engage B2C and D2C
                customers instantly and qualify prospects in real-time.
              </p>
            </div>

            <div className="b2cb2b-usecase-card" data-aos="fade-up" data-aos-delay="200">
              <div className="b2cb2b-usecase-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3>24/7 Support</h3>
              <p>
                Provide 24/7 ai based customer support without extra staffing. Ensure your B2C & D2C
                Brand Solutions run smoothly anytime.
              </p>
            </div>

            <div className="b2cb2b-usecase-card" data-aos="fade-up" data-aos-delay="300">
              <div className="b2cb2b-usecase-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <h3>Global Reach</h3>
              <p>
                Expand your global presence. AI Chat Assist supports B2C and D2C brands in SG, UK &
                UAE with multilingual, scalable chatbot solutions.
              </p>
            </div>

            <div className="b2cb2b-usecase-card" data-aos="fade-up" data-aos-delay="400">
              <div className="b2cb2b-usecase-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h3>Customer Engagement</h3>
              <p>
                Drive customer engagement and gain actionable AI Insights. Keep B2C and D2C users
                active, informed, and ready to convert.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Chat Features Section */}
      <section className="b2cb2b-features-section">
        <div className="b2cb2b-features-bg"></div>
        <div className="container">
          <div className="b2cb2b-features-header">
            <h2 data-aos="fade-up">
              AI Chat Assist <span className="b2cb2b-features-highlight">Features</span>
            </h2>
            <p data-aos="fade-up" data-aos-delay="100">
              Website AI Chatbot for your Business is built to help B2C and D2C brands automate
              support, drive lead generation, and offer personalized customer experiences—across
              every channel
            </p>
          </div>

          <div className="b2cb2b-features-container">
            <div className="b2cb2b-features-card" data-aos="zoom-in" data-aos-delay="200">
              <div className="b2cb2b-features-card-inner">
                <div className="b2cb2b-features-card-front">
                  <div className="b2cb2b-features-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                  <h3>Conversational AI</h3>
                </div>
                <div className="b2cb2b-features-card-back">
                  <p>
                    Engage B2C & D2C customers with natural, AI-driven conversations. Understand
                    context and respond instantly—just like a real assistant.
                  </p>
                  {/* <a href="#features" className="b2cb2b-features-learn-more">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>

            <div className="b2cb2b-features-card" data-aos="zoom-in" data-aos-delay="300">
              <div className="b2cb2b-features-card-inner">
                <div className="b2cb2b-features-card-front">
                  <div className="b2cb2b-features-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="9"></rect>
                      <rect x="14" y="3" width="7" height="5"></rect>
                      <rect x="14" y="12" width="7" height="9"></rect>
                      <rect x="3" y="16" width="7" height="5"></rect>
                      <path d="M10 7h4"></path>
                      <path d="M10 17h4"></path>
                    </svg>
                  </div>
                  <h3>Multi-Channel Support</h3>
                </div>
                <div className="b2cb2b-features-card-back">
                  <p>
                    Deliver seamless support across web, WhatsApp, social media, and more. Perfect
                    for managing B2C and D2C multi-channel presence.
                  </p>
                  {/* <a href="#features" className="b2cb2b-features-learn-more">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>

            <div className="b2cb2b-features-card" data-aos="zoom-in" data-aos-delay="400">
              <div className="b2cb2b-features-card-inner">
                <div className="b2cb2b-features-card-front">
                  <div className="b2cb2b-features-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                  </div>
                  <h3>Analytics Dashboard</h3>
                </div>
                <div className="b2cb2b-features-card-back">
                  <p>
                    Track performance and user behavior with our Analytics Dashboard. Get AI
                    Insights to refine B2C & D2C strategies.
                  </p>
                  {/* <a href="#features" className="b2cb2b-features-learn-more">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>

            <div className="b2cb2b-features-card" data-aos="zoom-in" data-aos-delay="500">
              <div className="b2cb2b-features-card-inner">
                <div className="b2cb2b-features-card-front">
                  <div className="b2cb2b-features-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
                      <line x1="16" y1="8" x2="2" y2="22"></line>
                      <line x1="17.5" y1="15" x2="9" y2="15"></line>
                    </svg>
                  </div>
                  <h3>Content Generation</h3>
                </div>
                <div className="b2cb2b-features-card-back">
                  <p>
                    Generate dynamic product messaging and marketing content. AI helps B2C and D2C
                    brands stay relevant and responsive.
                  </p>
                  {/* <a href="#features" className="b2cb2b-features-learn-more">
                    Learn More
                  </a> */}
                </div>
              </div>
            </div>
          </div>

          <div className="b2cb2b-features-cta" data-aos="fade-up" data-aos-delay="400">
            <a href="#keyfeatures" className="b2cb2b-features-button">
              Explore All Features <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container">
          <div className="section-header text-center fade-in" id="keyfeatures">
            <h2>
              {' '}
              <span className="hero-highlight">Key</span>{' '}
              <span className="highlight">Features</span>
            </h2>
            <p>Powerful capabilities for B2C and d2c brands</p>
          </div>

          <div className="platform-grid mt-5">
            <div className="platform-card fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3>Personalization Engine</h3>
              <p>
                Use AI to tailor every interaction. Perfect for B2C and D2C brands focused on
                building deeper, data-driven connections.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3>Multi-Channel Deployment</h3>
              <p>
                Centralize your presence across web, messaging apps, and social—ideal for B2C and
                D2C multi-channel engagement.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <h3>Advanced Analytics</h3>
              <p>
                Get clear AI Insights on user behavior and conversion trends. Make data-driven
                decisions for your B2C & D2C brand.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <h3>Enterprise Security</h3>
              <p>
                Keep your B2C and D2C customer data secure with enterprise-level protection and
                regional compliance.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
              </div>
              <h3>Seamless Integrations</h3>
              <p>
                Integrate WebsiteAI Chatbot for your Business with your full tech stack—CRM,
                eCommerce, and marketing tools.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.6s' }}>
              <div className="platform-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <h3>Global Capabilities</h3>
              <p>
                Support B2C & D2C brands globally with multilingual AI chatbots built for
                international compliance and reach.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.7s' }}>
              <div className="platform-icon pulse-animation">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
                  <line x1="16" y1="8" x2="2" y2="22"></line>
                  <line x1="17.5" y1="15" x2="9" y2="15"></line>
                </svg>
              </div>
              <h3>AI Content Creation</h3>
              <p>
                Automatically create personalized content and product messaging to power marketing
                campaigns with AI.
              </p>
            </div>

            <div className="platform-card fade-in-up" style={{ animationDelay: '0.8s' }}>
              <div className="platform-icon bounce-animation">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3>Automated Campaigns</h3>
              <p>
                Run automated, AI-driven campaigns with smart targeting—ideal for B2C & D2C
                conversion optimization.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default B2CB2B;
